import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { permissionDeniedTitle } from '@/lib/permissionUi';

/**
 * Shared human-escalation close panel.
 * Firefighter ID and Role Modification both collect the review note here,
 * then close the ticket through resolve-access-issue.
 */
export function HumanEscalationClosePanel({
  title,
  description,
  comment,
  onComment,
  onSubmit,
  submitting = false,
  canMutate = false,
  soft = false,
  commentLabel = 'Resolution comment',
  placeholder = 'How was this resolved? (required)',
  buttonLabel = 'Mark complete',
}) {
  return (
    <Card
      className={
        soft
          ? 'border-cp-mod bg-[color-mix(in_srgb,var(--mod)_14%,var(--surface))]'
          : 'border-cp-crit bg-[color-mix(in_srgb,var(--crit)_14%,var(--surface))]'
      }
      data-testid="workflow-access-issue-banner"
    >
      <CardHeader>
        <CardTitle className={`text-lg flex items-center gap-2 ${soft ? 'text-cp-mod' : 'text-cp-crit'}`}>
          <ShieldAlert className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription className={soft ? 'text-cp-mod/90' : 'text-cp-crit/90'}>
          {description}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="wf-resolve-comment" className={soft ? 'text-cp-mod' : 'text-cp-crit'}>
            {commentLabel}
          </Label>
          <Textarea
            id="wf-resolve-comment"
            value={comment}
            onChange={(event) => onComment(event.target.value)}
            placeholder={placeholder}
            className="bg-cp-surface min-h-[88px]"
            disabled={!canMutate || submitting}
            data-testid="workflow-resolve-access-issue-comment"
          />
        </div>
        <span title={permissionDeniedTitle(canMutate, 'mark access issue resolved')}>
          <Button
            onClick={onSubmit}
            disabled={submitting || !canMutate || !String(comment || '').trim()}
            className="bg-cp-ok hover:bg-cp-ok"
            data-testid="workflow-resolve-access-issue-btn"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            {buttonLabel}
          </Button>
        </span>
      </CardContent>
    </Card>
  );
}
